import { useState } from 'react'
import { KeyRound, ShieldCheck, Smartphone, X } from 'lucide-react'
import type { User } from 'firebase/auth'
import {
  beginTotpEnrollment,
  completeTotpEnrollment,
  hasPasswordProvider,
  hasTotpProvider,
  linkPasswordToCurrentUser,
  type TotpEnrollment,
} from '../lib/cloud'

interface SecurityModalProps {
  user: User
  onClose: () => void
}

function securityErrorMessage(reason: unknown): string {
  const code =
    typeof reason === 'object' && reason !== null && 'code' in reason
      ? String(reason.code)
      : ''

  switch (code) {
    case 'auth/invalid-verification-code':
      return 'Неверный код. Проверьте часы телефона и попробуйте новый код.'
    case 'auth/operation-not-allowed':
      return 'TOTP ещё не включён в настройках Firebase проекта.'
    case 'auth/popup-blocked':
      return 'Браузер заблокировал окно Google. Разрешите всплывающие окна и повторите.'
    case 'auth/popup-closed-by-user':
      return 'Подтверждение Google отменено.'
    case 'auth/maximum-second-factor-count-exceeded':
      return 'Достигнут лимит подключённых способов подтверждения.'
    case 'auth/network-request-failed':
      return 'Нет связи с сервером. Проверьте интернет и повторите.'
    case 'auth/too-many-requests':
      return 'Слишком много попыток. Подождите и повторите позже.'
    default:
      return reason instanceof Error ? reason.message : 'Не удалось изменить настройки безопасности.'
  }
}

export function SecurityModal({ user, onClose }: SecurityModalProps) {
  const [passwordLinked, setPasswordLinked] = useState(() => hasPasswordProvider(user))
  const [totpLinked, setTotpLinked] = useState(() => hasTotpProvider(user))
  const [password, setPassword] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [enrollment, setEnrollment] = useState<TotpEnrollment | null>(null)
  const [totpCode, setTotpCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  function closeModal() {
    setEnrollment(null)
    setTotpCode('')
    onClose()
  }

  async function linkPassword(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setError('')
    setMessage('')

    if (password.length < 12) {
      setError('Используйте пароль длиной не менее 12 символов.')
      return
    }
    if (password !== confirmation) {
      setError('Пароли не совпадают.')
      return
    }

    setLoading(true)
    try {
      const updatedUser = await linkPasswordToCurrentUser(password)
      setPasswordLinked(hasPasswordProvider(updatedUser))
      setPassword('')
      setConfirmation('')
      setMessage('Пароль привязан к текущему аккаунту. UID и база не изменились.')
    } catch (reason) {
      setError(securityErrorMessage(reason))
    } finally {
      setLoading(false)
    }
  }

  async function startTotpEnrollment() {
    setLoading(true)
    setError('')
    setMessage('')
    try {
      setEnrollment(await beginTotpEnrollment())
      setTotpCode('')
    } catch (reason) {
      setError(securityErrorMessage(reason))
    } finally {
      setLoading(false)
    }
  }

  async function finishTotpEnrollment(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!enrollment) return

    setLoading(true)
    setError('')
    setMessage('')
    try {
      const updatedUser = await completeTotpEnrollment(enrollment, totpCode)
      setTotpLinked(true)
      setEnrollment(null)
      setTotpCode('')
      setMessage(
        totpLinked
          ? 'Резервный Authenticator подключён.'
          : 'Двухфакторная защита включена. При следующем входе потребуется код.',
      )
    } catch (reason) {
      setError(securityErrorMessage(reason))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="modal-backdrop" role="presentation">
      <section className="modal security-modal" role="dialog" aria-modal="true" aria-labelledby="security-title">
        <div className="modal-header">
          <div>
            <span className="eyebrow">Аккаунт владельца</span>
            <h2 id="security-title">Безопасность входа</h2>
          </div>
          <button className="icon-button" type="button" onClick={closeModal} aria-label="Закрыть">
            <X size={19} />
          </button>
        </div>

        <div className="security-account">
          <ShieldCheck size={21} />
          <div>
            <strong>{user.email}</strong>
            <span>UID сохраняется: {user.uid}</span>
          </div>
        </div>

        {passwordLinked ? (
          <div className="security-success">
            <KeyRound size={20} />
            <div>
              <strong>Вход по паролю подключён</strong>
              <span>Почта подтверждена, данные доступны с прежним UID.</span>
            </div>
          </div>
        ) : (
          <form className="security-form" onSubmit={(event) => void linkPassword(event)}>
            <p>
              Задайте пароль для существующего Google-аккаунта. Новая учётная запись
              создаваться не будет.
            </p>
            <label>
              <span>Новый пароль — минимум 12 символов</span>
              <input
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
              />
            </label>
            <label>
              <span>Повторите пароль</span>
              <input
                type="password"
                autoComplete="new-password"
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
                required
              />
            </label>
            <button className="button primary full-width" type="submit" disabled={loading}>
              <KeyRound size={18} />
              {loading ? 'Привязываю…' : 'Привязать пароль к текущему аккаунту'}
            </button>
          </form>
        )}

        <div className="security-divider" />

        {totpLinked && !enrollment ? (
          <div className="security-factor-stack">
            <div className="security-success">
              <Smartphone size={20} />
              <div>
                <strong>Authenticator подключён</strong>
                <span>Код потребуется после входа и по паролю, и через Google.</span>
              </div>
            </div>
            <button className="button secondary full-width" type="button" disabled={loading} onClick={() => void startTotpEnrollment()}>
              <Smartphone size={18} />
              {loading ? 'Подготавливаю…' : 'Добавить резервный Authenticator'}
            </button>
          </div>
        ) : enrollment ? (
          <form className="security-form" onSubmit={(event) => void finishTotpEnrollment(event)}>
            <div>
              <strong>Добавьте WorkSmarter в Authenticator</strong>
              <p className="security-note">
                На телефоне нажмите кнопку ниже. На другом устройстве добавьте аккаунт вручную по секрету.
              </p>
            </div>
            <a className="button secondary full-width" href={enrollment.authenticatorUri}>
              <Smartphone size={18} /> Открыть в Authenticator
            </a>
            <div className="totp-secret-row">
              <code>{enrollment.secretKey}</code>
            </div>
            <label>
              <span>Код из Authenticator</span>
              <input
                autoFocus
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                pattern="[0-9]{6}"
                value={totpCode}
                onChange={(event) => setTotpCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                required
              />
            </label>
            <button className="button primary full-width" type="submit" disabled={loading || totpCode.length !== 6}>
              <ShieldCheck size={18} />
              {loading ? 'Проверяю…' : 'Включить двухфакторную защиту'}
            </button>
            <button
              className="text-button auth-mode-button"
              type="button"
              disabled={loading}
              onClick={() => {
                setEnrollment(null)
                setTotpCode('')
                setError('')
              }}
            >
              Отменить подключение
            </button>
          </form>
        ) : (
          <div className="security-form">
            <div>
              <strong>Двухфакторная защита</strong>
              <p className="security-note">
                После пароля или Google WorkSmarter запросит отдельный шестизначный код.
              </p>
            </div>
            <button className="button primary full-width" type="button" disabled={loading || !passwordLinked} onClick={() => void startTotpEnrollment()}>
              <Smartphone size={18} />
              {loading ? 'Подготавливаю…' : 'Подключить Authenticator'}
            </button>
          </div>
        )}

        {message ? <p className="form-success" role="status">{message}</p> : null}
        {error ? <p className="form-error" role="alert">{error}</p> : null}

        <p className="security-note">
          Firebase не выдаёт резервные коды. После первого входа подключите второй независимый Authenticator как резервный и не храните оба на одном устройстве.
        </p>
      </section>
    </div>
  )
}
